/* Public, no-auth certificate verification page.
 * Anyone with a cert ID (typically embedded in the PDF or shared by the
 * student) can paste it here to confirm authenticity.  Reads a single
 * read-only field set from /api/verify/cert/{cert_id} — no PII beyond the
 * student name + program. */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Award, ShieldCheck, ShieldAlert, ShieldOff, Search, ArrowRight,
} from 'lucide-react';
import ZheltaLogo from '../components/ZheltaLogo';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

function StatusBanner({ data }) {
  if (data === null) return null;
  if (data?.valid) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-start gap-3"
        data-testid="verify-status-valid">
        <ShieldCheck className="w-6 h-6 text-emerald-600 mt-0.5 shrink-0" />
        <div>
          <div className="text-[14px] font-extrabold text-emerald-800">Authentic — issued by ZHELTA Academy</div>
          <div className="text-[12.5px] text-emerald-700/90">
            This certificate is valid and currently active.
          </div>
        </div>
      </div>
    );
  }
  if (data?.reason === 'revoked') {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-5 flex items-start gap-3"
        data-testid="verify-status-revoked">
        <ShieldOff className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
        <div>
          <div className="text-[14px] font-extrabold text-red-800">Certificate revoked</div>
          <div className="text-[12.5px] text-red-700/90">
            This certificate was revoked on{' '}
            {data.revoked_at ? new Date(data.revoked_at).toLocaleDateString() : 'an earlier date'}{' '}
            and is no longer valid.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3"
      data-testid="verify-status-notfound">
      <ShieldAlert className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
      <div>
        <div className="text-[14px] font-extrabold text-amber-800">Certificate not found</div>
        <div className="text-[12.5px] text-amber-700/90">
          We couldn't find a certificate with that ID. Double-check the code and try again.
        </div>
      </div>
    </div>
  );
}

export default function VerifyCertificate() {
  const params = useParams();
  const [code, setCode] = useState(params.certId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const verify = async (id) => {
    if (!id) return;
    setLoading(true);
    setTouched(true);
    try {
      const { data: r } = await axios.get(`${API}/api/verify/cert/${encodeURIComponent(id)}`);
      setData(r);
    } catch {
      setData({ valid: false, reason: 'not_found' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify if certId came in via URL.
  useEffect(() => {
    if (params.certId) verify(params.certId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.certId]);

  return (
    <div className="min-h-screen text-white"
      style={{ background: 'radial-gradient(circle at 30% 0%, #1f1404 0%, #0e0e10 50%, #07070a 100%)' }}>
      <header className="max-w-[1100px] mx-auto px-4 lg:px-6 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="verify-home-link">
          <ZheltaLogo size={32} />
          <div className="leading-none">
            <div className="text-[16px] font-extrabold tracking-[0.2em]">ZHELTA</div>
            <div className="text-[9px] font-semibold tracking-[0.3em] text-[#f5a623]">ACADEMY</div>
          </div>
        </Link>
        <Link to="/academy" className="text-[12px] text-white/60 hover:text-[#c9a227] inline-flex items-center gap-1">
          Visit academy <ArrowRight className="w-3 h-3" />
        </Link>
      </header>

      <main className="max-w-[860px] mx-auto px-4 lg:px-6 py-8 sm:py-14">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10.5px] uppercase tracking-[0.22em] text-[#f5a623] font-bold mb-4">
            <Award className="w-3 h-3" /> Certificate Verification
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Verify a ZHELTA Academy certificate
          </h1>
          <p className="text-white/60 mt-3 max-w-[540px] mx-auto text-[14px]">
            Enter the certificate ID exactly as it appears on the document
            (format: <span className="font-mono text-[#f5a623]">ZHA-YYYY-XXXXXXXX</span>).
          </p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); verify(code.trim()); }}
          className="rounded-2xl bg-white text-[#0e0e10] p-5 sm:p-6 border border-white/10 shadow-2xl"
          data-testid="verify-form"
        >
          <label className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1.5">
            Certificate ID
          </label>
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[#6E6E73]" />
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ZHA-2026-XXXXXXXX"
              data-testid="verify-input"
              className="flex-1 outline-none border-b border-[#E5E5E7] focus:border-[#c9a227] font-mono text-[15px] py-2"
            />
            <button type="submit" disabled={!code || loading}
              data-testid="verify-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </div>

          {touched && !loading && (
            <div className="mt-5 space-y-4">
              <StatusBanner data={data} />

              {data?.valid && (
                <div className="rounded-2xl border border-[#E5E5E7] bg-[#FAFAFA] p-5"
                  data-testid="verify-details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Awarded to"  value={data.student_name} />
                    <Field label="Program"     value={data.program_name} />
                    <Field label="Completion"  value={new Date(data.completion_date || data.issued_date).toLocaleDateString(undefined, { dateStyle: 'long' })} />
                    <Field label="Issued"      value={new Date(data.issued_date).toLocaleDateString(undefined, { dateStyle: 'long' })} />
                    <Field label="Signed by"   value={`${data.signature_name} · ${data.signature_title}`} />
                    <Field label="Cert ID"     value={data.cert_id} mono />
                  </div>
                </div>
              )}
            </div>
          )}
        </form>

        <p className="text-center text-white/40 text-[11.5px] mt-8">
          ZHELTA Academy · Certificates are issued only to students who complete
          all required course content. Questions? Email{' '}
          <a href="mailto:careers@zhelta.com" className="text-[#c9a227]">careers@zhelta.com</a>.
        </p>
      </main>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-0.5">{label}</div>
      <div className={`text-[14px] font-bold text-[#0e0e10] ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}
